import React from 'react';
import PropTypes from 'prop-types';
import { TextField, Card, Typography, Autocomplete, Box } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import SelectDropdown from '../../../components/SelectDropdown';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '../../../components/Button';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import styles from './styles';
import OfferExpiryDate from './OfferExpiryDate';
import { AND, OR, PLUS } from '../constants';
import { useFinancialOffer } from '../hooks/useFinancialOffer';

const FinancialOffer = ({
  index,
  offersList = [],
  defaultExpiryDate,
  promotionData,
  setPromotionData,
  mode,
  isFinancialClickable,
  setSelectedConjunction,
  selectedConjunction,
  isAdmin,
  loadedPromotionData,
  selectedStores = [],
  historicPromotionData,
  isDateSelectionDisabled,
  fetchHeadlineData,
}) => {
  const {
    // State values
    showDatePickerForFinanceOfferID,
    setShowDatePickerForFinanceOfferID,
    offerApplicable,
    configureHeadline,
    selectVendor,
    selectHeadline,
    disableConfig,
    customHeadline,
    customSubHeadline,
    customDisclaimer,
    planID,
    promoExpiry,
    offerIdError,
    modifiedOfferData,
    vendorsListOptions,

    // Handler functions
    handleOfferInclude,
    handleConjuctionChange,
    handleCustomHeadline,
    handleCustomSubHeadline,
    handleCustomDisclaimer,
    handleHeadlineChange,
    handlePlanIDChange,
    handleVendorChange,
    handleAutocompleteChange,
    handleOfferExpiryDate,
    handleOfferExpiryDateForInstallation,
    handleScheduleExpDate,
    handlePurchaseExpDate,
    handleConfigureHeadline,
    renderModifiedHeadline,
  } = useFinancialOffer({
    index,
    offersList,
    defaultExpiryDate,
    promotionData,
    setPromotionData,
    mode,
    selectedStores,
    loadedPromotionData,
    historicPromotionData,
    setSelectedConjunction,
    fetchHeadlineData,
  });

  return (
    <section
      style={
        mode === 'Edit' && promotionData?.status === 'ARCHIVED' && isAdmin === false
          ? { marginTop: '20px', ...styles.sectionSpacing, ...styles.disableCard }
          : styles.sectionSpacing
      }
    >
      <Typography style={styles.heading}>Finance Offer</Typography>
      {!isFinancialClickable ? (
        <div style={{ position: 'relative', width: 'fit-content' }}>
          <Tooltip
            arrow
            placement="top"
            leaveDelay={500}
            sx={styles.freezeDateTooltip}
            title="Please select a branch to include this offer"
          >
            <FormGroup>
              <FormControlLabel control={<Checkbox checked={false} />} label="Include" />
            </FormGroup>
          </Tooltip>
        </div>
      ) : (
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={offerApplicable} onChange={handleOfferInclude} />}
            label="Include"
            sx={{ width: 'fit-content' }}
          />
        </FormGroup>
      )}
      <Card
        sx={
          !offerApplicable
            ? { marginTop: '20px', ...styles.cardBox, ...styles.disableCard }
            : { marginTop: '20px', ...styles.cardBox }
        }
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', padding: '0 20px' }}>
          <Typography sx={{ paddingRight: '20px' }}>Conjuction:</Typography>
          <RadioGroup
            value={selectedConjunction}
            aria-labelledby="radio-buttons-group-label"
            defaultValue={OR}
            name="conjuctions-radio-buttons-group"
            row
            onChange={(e) => handleConjuctionChange(e.target.value)}
          >
            <FormControlLabel value={AND} control={<Radio />} label={AND} />
            <FormControlLabel value={OR} control={<Radio />} label={OR} />
            <FormControlLabel value={PLUS} control={<Radio />} label={PLUS} />
          </RadioGroup>
        </div>
        <div className="finance-vendor" style={styles.divContainer}>
          <SelectDropdown
            label="Finance Vendor"
            options={vendorsListOptions || []}
            singleSelectDefaultValues=""
            handleFilterChange={handleVendorChange}
            updateSingleDropdownValue={selectVendor}
            width={'-webkit-fill-available'}
            containerWidth={'75%'}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginTop: '15px',
              width: '20%',
              flexDirection: 'column',
            }}
          >
            <TextField
              id="planId"
              label="Plan ID"
              variant="outlined"
              type="number"
              size="small"
              value={planID}
              onChange={(event) => {
                handlePlanIDChange(event.target.value);
              }}
              inputProps={{ min: 0 }}
              error={Number(planID) < 0 || offerIdError}
            />
            {(Number(planID) < 0 || offerIdError) && (
              <Typography variant="caption" color="error" component="div" sx={{ marginTop: '2px' }}>
                {(Number(planID) < 0 && 'Plan ID cannot be negative') ||
                  (offerIdError && 'Offer ID not found')}
              </Typography>
            )}
          </Box>
        </div>
        <div className="headline-dropdown" style={styles.divContainer}>
          <SelectDropdown
            label="Headline"
            options={modifiedOfferData || []}
            singleSelectDefaultValues=""
            handleFilterChange={handleHeadlineChange}
            updateSingleDropdownValue={selectHeadline}
            width={'-webkit-fill-available'}
            containerWidth={'65%'}
          />
          <Button
            sx={{ ...styles.buttonStyle, background: '#e0e0e0' }}
            disabled={disableConfig}
            startIcon={<EditIcon sx={{ paddingLeft: '5px' }} />}
            onClick={() => handleConfigureHeadline()}
          >
            Configure
          </Button>
        </div>
        {configureHeadline && (
          <div>
            {selectHeadline === 'custom_offer' ? (
              <div className="customHeadline" style={{ padding: '10px 20px' }}>
                <div>
                  <p style={{ ...styles.headingSmall, paddingTop: '5px' }}>
                    Headline: &nbsp;{' '}
                    <span style={{ background: 'rgb(255, 228, 51)', wordWrap: 'break-word' }}>
                      {customHeadline}
                    </span>
                  </p>
                  <TextField
                    variant="standard"
                    multiline
                    label="Type your own text"
                    value={customHeadline}
                    defaultValue={null}
                    onChange={(event) => {
                      handleCustomHeadline(event.target.value);
                    }}
                    fullWidth
                  />
                </div>
                <div style={{ paddingTop: '15px' }}>
                  <p style={{ ...styles.headingSmall, paddingTop: '5px' }}>
                    Subheadline: &nbsp;{' '}
                    <span style={{ background: 'rgb(255, 228, 51)', wordWrap: 'break-word' }}>
                      {customSubHeadline}
                    </span>
                  </p>
                  <TextField
                    variant="standard"
                    multiline
                    label="Type your own text (optional)"
                    value={customSubHeadline}
                    onChange={(event) => {
                      handleCustomSubHeadline(event.target.value);
                    }}
                    fullWidth
                  />
                </div>
                <div>
                  <p style={{ ...styles.headingSmall, paddingTop: '15px' }}>
                    Disclaimer: &nbsp;{' '}
                    <span style={{ background: 'rgb(255, 228, 51)', wordWrap: 'break-word' }}>
                      {customDisclaimer}
                    </span>
                  </p>
                  <TextField
                    variant="standard"
                    multiline
                    label="Type your own text (optional)"
                    value={customDisclaimer}
                    onChange={(event) => {
                      handleCustomDisclaimer(event.target.value);
                    }}
                    fullWidth
                  />
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', padding: '10px 20px', flexWrap: 'wrap' }}>
                <span style={{ ...styles.headingSmall, paddingTop: '5px' }}>Headline: &nbsp; </span>
                {selectHeadline !== 'none' &&
                  renderModifiedHeadline(offersList, selectHeadline).map((part) => {
                    if (part.type === 'autocomplete') {
                      return (
                        <React.Fragment key={part.key}>
                          &nbsp;
                          <Autocomplete
                            sx={{
                              '& .MuiOutlinedInput-input': {
                                height: '20px',
                              },
                              '& .MuiAutocomplete-inputRoot': {
                                paddingTop: 0,
                                paddingBottom: 0,
                              },
                              margin: '2px 4px',
                            }}
                            disableClearable
                            options={part.options}
                            value={part.value}
                            onInputChange={(event, newInputValue) =>
                              handleAutocompleteChange(newInputValue, part.part)
                            }
                            renderInput={({ inputProps, params, ...rest }) => (
                              <TextField
                                {...params}
                                sx={{ ' & .MuiInputBase-root': { width: '100px' } }}
                                {...rest}
                                inputProps={{ ...inputProps, readOnly: true }}
                              />
                            )}
                          />
                          &nbsp;
                        </React.Fragment>
                      );
                    } else {
                      return (
                        <span
                          key={part.key}
                          style={{ ...styles.headingSmall, padding: '5px 1px 0 1px' }}
                        >
                          {part.text}
                        </span>
                      );
                    }
                  })}
              </div>
            )}
            <div style={{ width: '100%', textAlign: 'center' }}></div>
          </div>
        )}
        <OfferExpiryDate
          showDatePickerForRegOfferID={showDatePickerForFinanceOfferID}
          setShowDatePickerForRegOfferID={setShowDatePickerForFinanceOfferID}
          handleOfferExpiryDate={handleOfferExpiryDate}
          handleOfferExpiryDateForInstallation={handleOfferExpiryDateForInstallation}
          handleScheduleExpDate={handleScheduleExpDate}
          handlePurchaseExpDate={handlePurchaseExpDate}
          promoExpiry={promoExpiry}
          offerApplicable={offerApplicable}
          defaultExpiryDate={defaultExpiryDate}
          promotionData={promotionData}
          isDateSelectionDisabled={isDateSelectionDisabled}
          disableConfig={disableConfig}
        ></OfferExpiryDate>
      </Card>
    </section>
  );
};

export default FinancialOffer;

FinancialOffer.propTypes = {
  // Required props
  index: PropTypes.number.isRequired,
  setPromotionData: PropTypes.func.isRequired,
  setSelectedConjunction: PropTypes.func.isRequired,
  // Optional props with default values
  offersList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      vendor: PropTypes.string,
      template: PropTypes.string,
      template_variables: PropTypes.arrayOf(PropTypes.string),
      nested_values: PropTypes.object,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          offer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          template_values: PropTypes.array,
        })
      ),
      restricted_stores: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      ),
    })
  ),
  defaultExpiryDate: PropTypes.instanceOf(Date),
  mode: PropTypes.oneOf(['Create', 'Edit']),
  isFinancialClickable: PropTypes.bool,
  selectedConjunction: PropTypes.oneOf(['AND', 'OR', 'PLUS']),
  isAdmin: PropTypes.bool,
  loadedPromotionData: PropTypes.bool,
  isDateSelectionDisabled: PropTypes.bool,
  selectedStores: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  // Complex nested structure for promotionData
  promotionData: PropTypes.shape({
    status: PropTypes.string,
    store_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    store_ids: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    conjunction: PropTypes.string,
    finance_offer: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.shape({
          headline_builder_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          headline: PropTypes.string,
          sub_headline: PropTypes.string,
          disclaimer: PropTypes.string,
          custom: PropTypes.bool,
          custom_values: PropTypes.object,
          vendor: PropTypes.string,
          offer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          offer_expiry: PropTypes.shape({
            expiry_dates: PropTypes.arrayOf(PropTypes.string),
            offer_expiry_type: PropTypes.oneOf([
              'EXPIRY_DATE',
              'LAST_INSTALLATION_DATE',
              'LAST_IN_HOME_CONSULTATION_AND_PURCHASE_DATE',
            ]),
          }),
        })
      ),
      PropTypes.object,
    ]),
  }),
  // Historic data structure
  historicPromotionData: PropTypes.shape({
    finance_offer: PropTypes.arrayOf(
      PropTypes.shape({
        offer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        headline_builder_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        headline: PropTypes.string,
        sub_headline: PropTypes.string,
        disclaimer: PropTypes.string,
        custom: PropTypes.bool,
        custom_values: PropTypes.object,
      })
    ),
  }),
  fetchHeadlineData: PropTypes.func.isRequired,
};
